import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  myTeamMonthlyEvolutionService,
  type MyTeamMonthlyEvolution,
} from '@/api/domains/attendance/myTeam';
import { useAbortController } from '@/hooks/ui/useAbortController';
import { useUI } from '@/hooks/ui/useUI';


export interface UseMyTeamMonthlyEvolutionResult {
  evolution: MyTeamMonthlyEvolution | null;
  isLoading: boolean;
}

// Data hook for the "Mi equipo" dashboard "Resumen del mes" — monthly evolution bar
// charts (ASIS-5413 / REXASIS-012). Recalculates whenever `companyId`/`monthlyCutId`
// change — i.e. whenever the Empresa/Año/Mes/Descripción filters of "Resumen del mes"
// resolve to a different company or cutoff. Mirrors useMyTeamMonthlyAbsenceSummary.ts:
// `monthlyCutId` can legitimately be null (no cutoffs available for the selected
// company, per ASIS-5414) — in that case this hook skips the fetch entirely instead of
// calling the backend with an invalid id.
export function useMyTeamMonthlyEvolution(
  companyId: number | null,
  monthlyCutId: number | null,
): UseMyTeamMonthlyEvolutionResult {
  const { t: translate } = useTranslation();
  const { showApiError } = useUI();
  const { createSignal } = useAbortController();

  const [evolution, setEvolution] = useState<MyTeamMonthlyEvolution | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (companyId == null || monthlyCutId == null) {
      setEvolution(null);
      setIsLoading(false);
      return;
    }

    const signal = createSignal();
    setIsLoading(true);

    void (async () => {
      const response = await myTeamMonthlyEvolutionService.getEvolution(
        { cutoffCompanyId: companyId, monthlyCutId },
        signal,
      );
      if (response.error?.code === 'ABORTED') return;
      setIsLoading(false);

      if (!response.success || !response.data) {
        showApiError(response, translate('attendance:dashboard.myTeam.monthlyEvolution.loadError'));
        return;
      }

      setEvolution(response.data);
    })();
  }, [companyId, monthlyCutId, createSignal, showApiError, translate]);

  return { evolution, isLoading };
}

export default useMyTeamMonthlyEvolution;
